import { useIntl } from 'react-intl';
import { FarmData } from '../../types/FarmData';
import { FallbackImage } from '../UploadImage/fallBack/FallBackImage';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import useMarketType from '../../utils/useMarketType';
import { publicPath } from 'enums/publicRoutes';
import CompanyType from 'enums/company-type';
import { qualityMarkLogoClassName } from 'utils/qualityMarkHelpers';
import { StyledFarmCard } from './style';

const VISIBLE_QUALITY_MARKS = 3;
const MOBILE_VISIBLE_QUALITY_MARKS = 2;

export interface FarmProps {
  farm?: FarmData;
  className?: string;
  plainLogoUrl?: string;
  hoverable?: boolean;
  mobileRow?: boolean;
  row?: boolean;
}

export const FarmCard = ({
  farm,
  className,
  plainLogoUrl,
  hoverable,
  mobileRow,
  row,
}: FarmProps) => {
  const intl = useIntl();
  const { isEmarket } = useMarketType();

  const name = farm?.brand ?? farm?.name;
  const path = isEmarket
    ? publicPath.farm(farm?.slug, farm?.id)
    : publicPath.wholesaleFarm(farm?.slug, farm?.id);
  const address =
    farm?.card_address ??
    farm?.actual_full_address ??
    farm?.registered_full_address;
  const categories = farm?.card_categories ?? [];
  const isCooperative =
    farm?.company_type === CompanyType.COOPERATIVE_SOCIETY_KB;
  const cooperativeLabel = intl.formatMessage({ id: 'farm_card.cooperative' });

  const qualityMarks = (farm?.quality_marks ?? []).filter(
    (mark, index, all) => all.findIndex((x) => x.id === mark.id) === index
  );
  const visibleMarks = qualityMarks.slice(0, VISIBLE_QUALITY_MARKS);
  const hiddenMarks = qualityMarks.slice(VISIBLE_QUALITY_MARKS);
  const mobileHiddenMarks = hiddenMarks.length
    ? qualityMarks.slice(MOBILE_VISIBLE_QUALITY_MARKS)
    : [];

  const classNames = [
    'farm-card',
    hoverable && 'hoverable',
    mobileRow && 'mobile-row',
    row && 'row',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <StyledFarmCard to={path} className={classNames}>
      <div className={'farm-card-cover'}>
        <FallbackImage
          className={'farm-card-logo'}
          src={plainLogoUrl ?? farm?.logo ?? farm?.images?.[0]?.url_original}
          type={'FarmCard'}
          preview={false}
          alt={name}
        />
        {farm?.is_top_farm && (
          <span className={'farm-card-top'}>
            {intl.formatMessage({ id: 'all_farm_card.favorite' })}
          </span>
        )}
      </div>

      {isCooperative && (
        <Tooltip title={cooperativeLabel}>
          <span className={'farm-card-cooperative'}>
            <img src={'/qualityMark/3-09 1.jpg'} alt={cooperativeLabel} />
          </span>
        </Tooltip>
      )}

      <div className={'farm-card-body'}>
        <p className={'farm-card-name'} title={name}>
          {name}
        </p>
        {!!address && (
          <div className={'farm-card-address'} title={address}>
            <span className={'farm-card-pin'}>
              <Icon name={'location-pin-icon'} type={'any'} />
            </span>
            <span>{address}</span>
          </div>
        )}
        {!!categories.length && (
          <p className={'farm-card-categories'}>{categories.join(' · ')}</p>
        )}
        <div className={'farm-card-footer'}>
          <span className={'farm-card-count'}>
            {intl.formatMessage(
              { id: 'farm_card.products_count' },
              { count: farm?.card_products_count ?? 0 }
            )}
          </span>
          <div className={'farm-card-qmarks'}>
            {visibleMarks.map((mark, index) => (
              <Tooltip key={mark.id} title={mark.name}>
                <span
                  className={
                    mobileHiddenMarks.length &&
                    index >= MOBILE_VISIBLE_QUALITY_MARKS
                      ? 'farm-card-qmark farm-card-qmark-desktop'
                      : 'farm-card-qmark'
                  }
                >
                  <FallbackImage
                    className={qualityMarkLogoClassName(mark.slug)}
                    src={mark.logo || ''}
                    type={'notFound'}
                    preview={false}
                    alt={mark.name}
                  />
                </span>
              </Tooltip>
            ))}
            {!!hiddenMarks.length && (
              <Tooltip title={hiddenMarks.map((mark) => mark.name).join(', ')}>
                <span className={'farm-card-qmark farm-card-qmark-desktop'}>
                  +{hiddenMarks.length}
                </span>
              </Tooltip>
            )}
            {!!mobileHiddenMarks.length && (
              <Tooltip
                title={mobileHiddenMarks.map((mark) => mark.name).join(', ')}
              >
                <span className={'farm-card-qmark farm-card-qmark-mobile'}>
                  +{mobileHiddenMarks.length}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </StyledFarmCard>
  );
};
